import React from 'react';
import { Helmet } from 'react-helmet-async';

const SentimentAnalysis: React.FC = () => {
  // Mock data for sentiment analysis
  const sentimentData = {
    overallSentiment: {
      positive: 68,
      neutral: 24,
      negative: 8,
      score: 0.72
    },
    recentComments: [
      {
        id: 1,
        text: "Amazing product! The AI optimization really improved our website performance.",
        sentiment: 'positive',
        confidence: 0.94,
        source: 'Website Review',
        date: '2025-08-12'
      },
      {
        id: 2,
        text: "The platform is easy to use and the results are impressive.",
        sentiment: 'positive',
        confidence: 0.87,
        source: 'Social Media',
        date: '2025-08-11'
      },
      {
        id: 3,
        text: "Good features but could use more customization options.",
        sentiment: 'neutral',
        confidence: 0.76,
        source: 'Customer Feedback',
        date: '2025-08-10'
      },
      {
        id: 4,
        text: "The pricing is a bit high for small businesses.",
        sentiment: 'negative',
        confidence: 0.82,
        source: 'Review Site',
        date: '2025-08-09'
      },
      {
        id: 5,
        text: "Excellent customer support and fast response times!",
        sentiment: 'positive',
        confidence: 0.91,
        source: 'Support Ticket',
        date: '2025-08-08'
      }
    ],
    sentimentTrends: [
      { date: '2025-08-01', positive: 65, neutral: 26, negative: 9 },
      { date: '2025-08-02', positive: 67, neutral: 25, negative: 8 },
      { date: '2025-08-03', positive: 66, neutral: 27, negative: 7 },
      { date: '2025-08-04', positive: 69, neutral: 24, negative: 7 },
      { date: '2025-08-05', positive: 68, neutral: 25, negative: 7 },
      { date: '2025-08-06', positive: 70, neutral: 23, negative: 7 },
      { date: '2025-08-07', positive: 68, neutral: 24, negative: 8 }
    ],
    topKeywords: [
      { keyword: 'AI optimization', sentiment: 'positive', mentions: 45, impact: 'high' },
      { keyword: 'user interface', sentiment: 'positive', mentions: 32, impact: 'medium' },
      { keyword: 'pricing', sentiment: 'negative', mentions: 28, impact: 'high' },
      { keyword: 'customer support', sentiment: 'positive', mentions: 25, impact: 'medium' },
      { keyword: 'performance', sentiment: 'positive', mentions: 22, impact: 'high' }
    ]
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      case 'neutral': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case 'positive': return 'fas fa-smile';
      case 'negative': return 'fas fa-frown';
      case 'neutral': return 'fas fa-meh';
      default: return 'fas fa-meh';
    }
  };

  return (
    <>
      <Helmet>
        <title>Sentiment Analysis - Dream Digi Platform</title>
      </Helmet>
      
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Sentiment Analysis</h1>
          <p className="text-gray-600">AI-powered sentiment tracking and analysis of customer feedback</p>
        </div>

        {/* Overall Sentiment Score */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Overall Sentiment</p>
                <p className="text-2xl font-bold text-gray-900">{sentimentData.overallSentiment.score.toFixed(2)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <i className="fas fa-chart-pie text-blue-600 text-xl"></i>
              </div>
            </div>
            <div className="mt-4">
              <span className="text-green-600 text-sm font-medium">+0.05</span>
              <span className="text-gray-500 text-sm ml-1">from last week</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Positive</p>
                <p className="text-2xl font-bold text-green-600">{sentimentData.overallSentiment.positive}%</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <i className="fas fa-smile text-green-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Neutral</p>
                <p className="text-2xl font-bold text-gray-600">{sentimentData.overallSentiment.neutral}%</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-full">
                <i className="fas fa-meh text-gray-600 text-xl"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Negative</p>
                <p className="text-2xl font-bold text-red-600">{sentimentData.overallSentiment.negative}%</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <i className="fas fa-frown text-red-600 text-xl"></i>
              </div>
            </div>
          </div>
        </div>

        {/* Sentiment Trends Chart */}
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Sentiment Trends (Last 7 Days)</h3>
          <div className="space-y-4">
            {sentimentData.sentimentTrends.map((trend, index) => (
              <div key={index} className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">{trend.date}</span>
                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-green-600">{trend.positive}%</p>
                    <p className="text-xs text-gray-500">Positive</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-600">{trend.neutral}%</p>
                    <p className="text-xs text-gray-500">Neutral</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-red-600">{trend.negative}%</p>
                    <p className="text-xs text-gray-500">Negative</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Comments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Comments</h3>
            <div className="space-y-4">
              {sentimentData.recentComments.map((comment) => (
                <div key={comment.id} className="border-l-4 border-gray-200 pl-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(comment.sentiment)}`}>
                      <i className={`${getSentimentIcon(comment.sentiment)} mr-1`}></i>
                      {comment.sentiment.charAt(0).toUpperCase() + comment.sentiment.slice(1)}
                    </span>
                    <span className="text-xs text-gray-500">{comment.date}</span>
                  </div>
                  <p className="text-sm text-gray-900 mb-2">{comment.text}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Source: {comment.source}</span>
                    <span>Confidence: {(comment.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Keywords */}
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Keywords</h3>
            <div className="space-y-4">
              {sentimentData.topKeywords.map((keyword, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSentimentColor(keyword.sentiment)}`}>
                      <i className={`${getSentimentIcon(keyword.sentiment)} mr-1`}></i>
                      {keyword.sentiment.charAt(0).toUpperCase() + keyword.sentiment.slice(1)}
                    </span>
                    <span className="text-sm font-medium text-gray-900">{keyword.keyword}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">{keyword.mentions} mentions</p>
                    <p className="text-xs text-gray-500">Impact: {keyword.impact}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i className="fas fa-robot text-purple-600 mr-2"></i>
            AI Sentiment Insights
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <h4 className="font-medium text-gray-900 mb-2">Positive Trends</h4>
              <p className="text-sm text-gray-600">Customer satisfaction is trending upward, with 68% positive sentiment. Focus on maintaining quality and support.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <h4 className="font-medium text-gray-900 mb-2">Areas for Improvement</h4>
              <p className="text-sm text-gray-600">Pricing concerns are the main negative feedback. Consider tiered pricing or value demonstration.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <h4 className="font-medium text-gray-900 mb-2">Brand Perception</h4>
              <p className="text-sm text-gray-600">AI optimization and performance are strongly associated with positive sentiment. Leverage this in marketing.</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-purple-200">
              <h4 className="font-medium text-gray-900 mb-2">Customer Engagement</h4>
              <p className="text-sm text-gray-600">High engagement on social media with positive sentiment. Consider expanding social media presence.</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SentimentAnalysis;
